import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEffect, useRef, useState, useCallback } from "react";
import { Typography } from "../ui/typography";

interface TruncatedTextTooltipProps {
  text: string;
  className?: string;
  maxLines?: number;
  children?: React.ReactNode;
  alwaysShow?: boolean;
}

export function TruncatedTextTooltip({
  text,
  className = "",
  maxLines = 1,
  children,
  alwaysShow = false,
}: TruncatedTextTooltipProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const element = textRef.current;
    if (element) {
      // For children, check the first child element (Typography)
      const targetElement = children
        ? (element.firstElementChild as HTMLElement) || element
        : element;

      const truncated =
        targetElement.scrollWidth > targetElement.clientWidth ||
        targetElement.scrollHeight > targetElement.clientHeight;

      setIsTruncated(truncated);
    }
  }, [children]);

  useEffect(() => {
    const el = textRef.current;

    // ResizeObserver fires when the element's size is first calculated and on
    // any subsequent layout changes (container resize, column count change, etc.)
    // — more reliable than setTimeout(0) which fires before layout has settled.
    let observer: ResizeObserver | null = null;
    if (el) {
      observer = new ResizeObserver(checkTruncation);
      observer.observe(el);
    }

    window.addEventListener("resize", checkTruncation);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", checkTruncation);
    };
  }, [text, checkTruncation]);

  const truncateClass = maxLines === 1 ? "truncate" : `line-clamp-${maxLines}`;

  if (!isTruncated && !alwaysShow) {
    return (
      <div ref={textRef} className={className}>
        {children || <div className={truncateClass}>{text}</div>}
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div ref={textRef} className={`${className} cursor-pointer`}>
            {children || <div className={truncateClass}>{text}</div>}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side='top'
          className='max-w-xs break-words z-50 bg-gray-700 text-grey-100'>
          <Typography variant='regular-12' className='text-gray-100'>
            {text}
          </Typography>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
